/**
 * Sessions et CR% dans le temps : sessions GA4, commandes Shopify. Tant que
 * GA4 n'a rien synchronisé, les sessions Shopify prennent le relais (`source`).
 *
 * ── Deux axes, à la demande de l'équipe ─────────────────────────────────────
 * Exception à la règle « jamais de double axe » du dashboard : l'équipe veut
 * retrouver le rendu de son ancien Looker, sessions en courbe (axe gauche) et
 * CR% en barres (axe droit). Les formes diffèrent, donc on ne lit pas de
 * croisement entre les deux ; les échelles restent indépendantes.
 *
 * CR% d'une semaine = commandes de la semaine ÷ sessions de la semaine, jamais
 * la moyenne des taux quotidiens. Un jour sans sessions lues n'a pas de point.
 */

import type { SalesDailyPoint } from '@shared/types/sales';
import { useMemo } from 'react';
import { Bar, CartesianGrid, ComposedChart, Line, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';

import { useChartMarkers } from '../hooks/useChartMarkers';
import { formatCompact, formatDayLong, formatDayShort, formatInteger, formatPercent } from '../lib/format';
import { bucketSizeFor } from '../lib/sales';
import { AXIS_STYLE, BAR_RADIUS, CATEGORICAL_COLORS, COLORS, LINE_WIDTH } from '../lib/theme';
import type { DataColumn } from '../types';
import { ChartCard } from './Card';
import { ChartLegend } from './ChartTooltip';
import { MarkerStrip } from './MarkerStrip';

const SESSIONS_COLOR = COLORS.series1;
const CR_COLOR = CATEGORICAL_COLORS[4];

interface Bucket {
  date: string;
  sessions: number | null;
  orders: number;
  cr: number | null;
}

/** Marqueurs « ventes » : impact lu sur les sessions. */
const MARKER_CONFIG = {
  value: (bucket: Bucket) => bucket.sessions,
  mode: 'level',
  unit: 'count',
  higherIsBetter: true,
  scope: 'sales',
} as const;

function bucketLabel(date: string, bucketSize: number): string {
  if (bucketSize === 1) return formatDayLong(date);
  return `${bucketSize === 7 ? 'Semaine' : '30 jours'} à partir du ${formatDayLong(date)}`;
}

function BucketTooltip({ active, payload, bucketSize }: { active?: boolean; payload?: Array<{ payload?: Bucket }>; bucketSize: number }) {
  const bucket = payload?.[0]?.payload;
  if (!active || !bucket) return null;
  return (
    <div className="tooltip" role="tooltip">
      <p className="tooltip__title">{bucketLabel(bucket.date, bucketSize)}</p>
      <div className="tooltip__row">
        <span className="legend__key" style={{ background: SESSIONS_COLOR }} aria-hidden="true" />
        <span className="tooltip__name">Sessions</span>
        <span className="tooltip__value">{bucket.sessions !== null ? formatInteger(bucket.sessions) : '—'}</span>
      </div>
      <div className="tooltip__row">
        <span className="legend__key legend__key--area" style={{ background: CR_COLOR }} aria-hidden="true" />
        <span className="tooltip__name">CR%</span>
        <span className="tooltip__value">{bucket.cr !== null ? formatPercent(bucket.cr) : '—'}</span>
      </div>
      <p className="tooltip__title">{formatInteger(bucket.orders)} commandes</p>
    </div>
  );
}

export function SessionsCrChart({
  daily,
  source = 'shopify',
}: {
  daily: readonly SalesDailyPoint[];
  /** D'où viennent les sessions de `daily` : GA4, ou Shopify en secours. */
  source?: 'ga4' | 'shopify';
}) {
  const bucketSize = bucketSizeFor(daily.length);

  const buckets = useMemo<Bucket[]>(() => {
    const out: Bucket[] = [];
    for (let start = 0; start < daily.length; start += bucketSize) {
      const slice = daily.slice(start, start + bucketSize);
      const first = slice[0];
      if (!first) continue;
      const measured = slice.filter((day) => day.sessions !== null && day.sessions !== undefined);
      const sessions = measured.length > 0 ? measured.reduce((sum, day) => sum + (day.sessions ?? 0), 0) : null;
      // CR% sur les seuls jours où les sessions sont connues.
      const orders = measured.reduce((sum, day) => sum + day.orders, 0);
      out.push({
        date: first.date,
        sessions,
        orders,
        cr: sessions !== null && sessions > 0 ? Math.round((orders / sessions) * 10000) / 100 : null,
      });
    }
    return out;
  }, [daily, bucketSize]);

  const markers = useChartMarkers(buckets, MARKER_CONFIG);

  const columns: ReadonlyArray<DataColumn<Bucket>> = [
    { key: 'date', header: bucketSize === 1 ? 'Jour' : 'À partir du', render: (row) => formatDayLong(row.date) },
    { key: 'sessions', header: 'Sessions', numeric: true, render: (row) => (row.sessions !== null ? formatInteger(row.sessions) : '—') },
    { key: 'orders', header: 'Commandes', numeric: true, render: (row) => formatInteger(row.orders) },
    { key: 'cr', header: 'CR%', numeric: true, render: (row) => (row.cr !== null ? formatPercent(row.cr) : '—') },
  ];

  const axisTick = { fill: AXIS_STYLE.fill, fontSize: AXIS_STYLE.fontSize };
  const hasData = buckets.some((bucket) => bucket.sessions !== null);
  const granularity = bucketSize === 1 ? 'par jour' : bucketSize === 7 ? 'par semaine' : 'par tranche de 30 jours';

  return (
    <ChartCard
      footer={<MarkerStrip items={markers.items} />}
      title="GA4 metrics over time"
      subtitle={`Sessions du site et taux de conversion (commandes Shopify ÷ sessions), ${granularity} — sessions ${source === 'ga4' ? 'Google Analytics' : 'Shopify (GA4 pas encore synchronisé)'}`}
      className="chart-grid__wide"
      rows={hasData ? buckets : []}
      columns={columns}
      emptyMessage="Sessions pas encore lues pour cette période."
      chartHeight={320}
    >
      <ChartLegend
        entries={[
          { label: 'Sessions', color: SESSIONS_COLOR },
          { label: 'CR%', color: CR_COLOR, shape: 'area' },
        ]}
      />
      <ResponsiveContainer width="100%" height="86%">
        <ComposedChart data={buckets} margin={{ top: 8, right: 12, bottom: 0, left: 0 }}>
          <CartesianGrid stroke={COLORS.grid} vertical={false} />
          <XAxis dataKey="date" tickFormatter={formatDayShort} tick={axisTick} axisLine={{ stroke: COLORS.grid }} tickLine={false} minTickGap={32} />
          {/* Deux axes, à la demande de l'équipe (même rendu que l'ancien Looker) : sessions à gauche, CR% à droite. */}
          {/* Axe gauche = axe par défaut : les lignes de marqueurs ne portent pas d'identifiant d'axe. */}
          <YAxis tickFormatter={formatCompact} tick={axisTick} axisLine={false} tickLine={false} width={56} />
          <YAxis
            yAxisId="cr"
            orientation="right"
            tickFormatter={(value: number) => formatPercent(value)}
            tick={axisTick}
            axisLine={false}
            tickLine={false}
            width={60}
            domain={[0, 'auto']}
          />
          <Tooltip cursor={{ fill: COLORS.grid, opacity: 0.4 }} content={<BucketTooltip bucketSize={bucketSize} />} />
          {markers.lines}
          <Bar yAxisId="cr" dataKey="cr" name="CR%" fill={CR_COLOR} fillOpacity={0.75} radius={[BAR_RADIUS, BAR_RADIUS, 0, 0]} maxBarSize={48} isAnimationActive={false} />
          <Line
            type="monotone"
            dataKey="sessions"
            name="Sessions"
            stroke={SESSIONS_COLOR}
            strokeWidth={LINE_WIDTH}
            dot={buckets.length <= 62 ? { r: 4, fill: SESSIONS_COLOR, stroke: COLORS.surface, strokeWidth: 2 } : false}
            activeDot={{ r: 5, fill: SESSIONS_COLOR, stroke: COLORS.surface, strokeWidth: 2 }}
            connectNulls={false}
            isAnimationActive={false}
          />
        </ComposedChart>
      </ResponsiveContainer>
    </ChartCard>
  );
}
